import LtrProvider from '@Components/Common/LtrProvider';
import useLSCheckout from '@hooks/useLSCheckout';
import {
    AddRounded,
    CloseRounded,
    CreditCardRounded,
    RemoveRounded,
} from '@mui/icons-material';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Typography,
    Stack,
    IconButton,
    Slider,
    Box,
    Button,
    Card,
    CardContent,
} from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

const PRICE_PER_1K = 5.99;

const BuyCredits = ({ open, onClose }) => {
    const { t } = useTranslation();
    const openCheckout = useLSCheckout({ onSuccess: onClose });

    const [amount, setAmount] = useState(1000);
    const step = 1000;
    const marks = [
        {
            value: 2000,
            label: '1,000',
        },
        {
            value: 10000,
            label: '10.000',
        },
        {
            value: 19000,
            label: '20,000',
        },
    ];

    return (
        <Dialog
            PaperProps={{
                sx: {
                    backgroundColor: 'surfaceContainerLow.main',
                    // minHeight: 'calc(100% - 64px)',
                },
            }}
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="xs"
        >
            <DialogTitle>{t('Buy Credits')}</DialogTitle>
            <IconButton
                aria-label="close"
                onClick={onClose}
                sx={theme => ({
                    position: 'absolute',
                    right: 16,
                    top: 16,
                    color: theme.palette.grey[500],
                })}
            >
                <CloseRounded />
            </IconButton>
            <DialogContent sx={{ textAlign: 'center', p: 2 }}>
                <Stack spacing={3}>
                    <Box>
                        <Typography variant="labelLarge" color="text.secondary">
                            {t('Select Amount')}
                        </Typography>
                        <LtrProvider>
                            <Stack
                                direction="row"
                                alignItems="center"
                                justifyContent="center"
                                spacing={3}
                                my={2}
                            >
                                <IconButton
                                    onClick={() =>
                                        setAmount(Math.max(0, amount - step))
                                    }
                                    disabled={amount <= 1000}
                                >
                                    <Button variant="tonal" color="surface">
                                        <RemoveRounded />
                                    </Button>
                                </IconButton>
                                <Typography
                                    variant="displayLarge"
                                    component="span"
                                    fontWeight="bold"
                                    sx={{ width: '8rem', textAlign: 'center' }}
                                >
                                    {amount / 1000}k
                                </Typography>
                                <IconButton
                                    onClick={() => setAmount(amount + step)}
                                    disabled={amount >= 20000}
                                >
                                    <Button variant="tonal" color="surface">
                                        <AddRounded />
                                    </Button>
                                </IconButton>
                            </Stack>
                        </LtrProvider>
                        <Typography variant="bodySmall" color="text.secondary">
                            ({amount.toLocaleString()} {t('Credits')})
                        </Typography>
                        <Box px={2} mt={3}>
                            <LtrProvider>
                                <Slider
                                    min={1000}
                                    max={20000}
                                    step={step}
                                    value={amount}
                                    onChange={(_, newValue) =>
                                        setAmount(newValue as number)
                                    }
                                    sx={{
                                        '& .MuiSlider-rail': {
                                            color: 'divider',
                                            height: 8,
                                        },
                                        '& .MuiSlider-mark': {
                                            display: 'none',
                                        },
                                    }}
                                    marks={marks}
                                    track={false}
                                />
                            </LtrProvider>
                        </Box>
                    </Box>
                    <Card
                        sx={{
                            p: 1,
                            textAlign: 'left',
                            bgcolor: 'secondaryContainer.main',
                        }}
                    >
                        <CardContent>
                            <Stack
                                direction="row"
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <Box>
                                    <Typography
                                        variant="bodySmall"
                                        color="text.secondary"
                                    >
                                        {t('Total Price')}
                                    </Typography>
                                    <Typography
                                        variant="titleLarge"
                                        fontWeight={700}
                                        color="text.primary"
                                    >
                                        $
                                        {(
                                            (amount / 1000) *
                                            PRICE_PER_1K
                                        ).toFixed(2)}
                                    </Typography>
                                </Box>
                                <Box textAlign="right">
                                    <Typography
                                        variant="labelMedium"
                                        color="text.secondary"
                                    >
                                        {t('Rate')}
                                    </Typography>
                                    <Typography
                                        variant="bodySmall"
                                        color="text.secondary"
                                    >
                                        ${PRICE_PER_1K}/1k
                                    </Typography>
                                </Box>
                            </Stack>
                        </CardContent>
                    </Card>
                    <Button
                        onClick={() =>
                            openCheckout({ quantity: amount / 1000 })
                        }
                        size="extraLarge"
                        startIcon={<CreditCardRounded />}
                    >
                        {t('Buy Credits')}
                    </Button>
                </Stack>
            </DialogContent>
        </Dialog>
    );
};

export default BuyCredits;
